/* eslint-disable */
import React from 'react'
import { useNavigate } from 'react-router-dom'
import EasyReconfigureModal, { EasyReconfigureContent } from '../features/reconfigure/EasyReconfigureModal'
import {
  ORG_HOMEPAGE_RECONFIGURE_FEATURES,
  ORG_HOMEPAGE_RECONFIGURE_PURPOSES,
  buildOrgHomepageReconfigureState,
  getDefaultOrgHomepageFeatureValues,
  getOrgHomepagePurposeFromState,
} from '../features/reconfigure/reconfigurePresets'
import { buildNavigationFromSections, cloneSection, normalizeLandingPageData, updateSectionTemplate } from '../features/orgHomepage/normalize'
import {
  BUILTIN_ORG_HOMEPAGE_IMAGE_ASSETS,
  SECTION_TEMPLATES,
  SECTION_TYPE_LABELS,
  createDefaultLandingPagePreset,
  createSectionDraft,
  getSectionMeta,
} from '../features/orgHomepage/registry'
import { api, canonicalStoredFileIdForApiPath } from '../lib/api'
import { formatApiDateTimeDisplay } from '../lib/date'
import { collectMarketingStrings } from '../lib/collectMarketingStrings'
import { initKakao } from '../lib/kakao'
import { orgLandingOgShareUrl } from '../lib/publicShareUrls'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { useToast } from '../ui/ToastProvider'
import ComplianceTextCheckButton from './ComplianceTextCheckButton'
import MenuGuideButton from './MenuGuideButton'
import OrgLandingPageViewer from './OrgLandingPageViewer'
import PersonalLandingPreviewViewport from './PersonalLandingPreviewViewport'
import ShareLinkActions from './ShareLinkActions'

function createDefaultPageState() {
  return normalizeLandingPageData(createDefaultLandingPagePreset())
}

function getPreviewImageUrl(fileId) {
  if (!fileId) return ''
  const raw = String(fileId)
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/')) {
    return raw
  }
  const apiBase = (api?.defaults?.baseURL || '').replace(/\/$/, '')
  return `${apiBase}/files/${encodeURIComponent(canonicalStoredFileIdForApiPath(raw))}/preview`
}

/** 섹션·슬롯별 업로드 시 권장 해상도(가이드 문구만; 강제 검증 아님) */
const ORG_SECTION_ASSET_IMAGE_GUIDE = {
  'hero:backgroundImage': '권장 1920×1080px(16:9) 이상, 최소 가로 1200px. 화면 폭에 맞게 상·하가 잘릴 수 있습니다.',
  'ga-intro:mainImage': '권장 1200×900px(4:3) 또는 가로 1200px 이상. 텍스트 옆 대표 이미지로 표시됩니다.',
  'ceo-message:mainImage': '권장 1000×1000px 이상(정사각~약간 세로). 원형/라운드 크롭에 맞게 잘립니다.',
  'business-intro:mainImage': '권장 1200×800px(3:2) 이상.',
  'lead-form:sideImage': '권장 800×1200px(세로) 전후. 폼 옆 보조 이미지로 좁은 영역에 맞춰집니다.',
}

function getOrgSectionAssetImageGuide(sectionType, slotKey) {
  const key = `${sectionType}:${slotKey}`
  return ORG_SECTION_ASSET_IMAGE_GUIDE[key] || ''
}

function formatDateTime(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const ORG_SECTION_WRITE_DESCRIPTIONS = {
  hero: '첫 화면 제목, 설명, 배경 이미지를 정리합니다.',
  'ga-intro': '조직 소개와 대표 이미지를 입력합니다.',
  'ceo-message': '대표 메시지를 작성합니다.',
  'business-intro': '사업단이나 팀 구조를 보여줍니다.',
  benefits: '입사 혜택과 장점을 정리합니다.',
  stats: '조직 규모나 성과를 숫자로 보여줍니다.',
  'lead-form': '입사/문의 폼 문구를 정리합니다.',
  contact: '주소, 전화, 이메일, 상담 링크를 입력합니다.',
}

function getOrgWritingItems(pageState = {}) {
  return (pageState.sections || []).map((section, index) => ({
    id: section?.id || `${section?.type || 'section'}-${index}`,
    label: SECTION_TYPE_LABELS[section?.type] || '섹션',
    description: ORG_SECTION_WRITE_DESCRIPTIONS[section?.type] || '문구와 표시 내용을 정리합니다.',
  }))
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{label}</div>
      <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
    </div>
  )
}

function TemplatePreviewCard({ template, active, onSelect, compact = false }) {
  if (!template) return null
  if (compact) {
    return (
      <button
        type="button"
        onClick={onSelect}
        className={`flex w-full min-w-0 gap-2.5 overflow-hidden rounded-lg border px-2 py-2 text-left transition ${active ? 'border-brand-navy bg-sky-50/80 shadow-sm ring-1 ring-brand-navy/20' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/80'
          }`}
      >
        <div className="relative h-11 w-[4.5rem] shrink-0 overflow-hidden rounded-md bg-slate-100">
          {template.previewImageFileId ? (
            <img src={getPreviewImageUrl(template.previewImageFileId)} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-[10px] text-slate-400">없음</div>
          )}
        </div>
        <div className="min-w-0 flex-1 py-0.5">
          <div className="flex items-start justify-between gap-1.5">
            <span className="text-xs font-semibold leading-tight text-slate-900">{template.name}</span>
            {active ? (
              <span className="shrink-0 rounded border border-brand-navy/20 bg-sky-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-brand-navy">선택</span>
            ) : null}
          </div>
          <p className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-slate-500">{template.description || '설명 없음'}</p>
        </div>
      </button>
    )
  }
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`max-w-[240px] overflow-hidden rounded-2xl border text-left transition ${active ? 'border-brand-navy bg-sky-50/60 shadow-sm ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'}`}
    >
      <div className="aspect-[16/8] bg-slate-100">
        {template.previewImageFileId ? (
          <img src={getPreviewImageUrl(template.previewImageFileId)} alt={template.name} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-slate-400">미리보기 없음</div>
        )}
      </div>
      <div className="space-y-2 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-900">{template.name}</div>
          {active ? <span className="rounded-full border border-brand-navy/20 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-brand-navy">선택됨</span> : null}
        </div>
        <div className="line-clamp-2 text-[11px] leading-5 text-slate-500">{template.description || '설명이 아직 등록되지 않았습니다.'}</div>
      </div>
    </button>
  )
}

function SectionOrderHandle({ currentIndex, dragging = false, compact = false, showIndex = true }) {
  const sizeClass = compact
    ? showIndex
      ? 'mt-0 min-w-[60px] gap-1.5 rounded-lg px-2 py-1 text-[10px]'
      : 'mt-0 min-w-[2rem] justify-center gap-0 rounded-lg px-1.5 py-1 text-[10px]'
    : 'mt-0.5 min-w-[76px] gap-2 rounded-2xl px-3 py-2 text-xs'
  return (
    <button
      type="button"
      draggable
      className={`flex cursor-grab items-center font-semibold transition active:cursor-grabbing ${sizeClass} ${dragging
          ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
          : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-white'
        } border`}
      title="드래그해서 순서를 바꿀 수 있습니다."
    >
      <span className={`tracking-[0.15em] ${compact ? 'text-[9px]' : 'text-[10px]'}`}>:::</span>
      {showIndex ? (
        <span className="tabular-nums">{compact ? currentIndex + 1 : `순서 ${currentIndex + 1}`}</span>
      ) : null}
    </button>
  )
}

function resolveDropPosition(event) {
  const rect = event.currentTarget.getBoundingClientRect()
  const midpoint = rect.top + rect.height / 2
  return event.clientY <= midpoint ? 'before' : 'after'
}

function sortSections(sections = []) {
  return [...sections]
    .map((section, index) => ({ ...section, order: Number(section?.order ?? index) }))
    .sort((a, b) => a.order - b.order)
    .map((section, index) => ({ ...section, order: index }))
}

/** 배열 순서만 바꿀 때 각 섹션의 order 필드를 같이 맞춰야 sortSections가 순서를 되돌리지 않습니다. */
function withSequentialSectionOrder(sections = []) {
  return (sections || []).map((section, index) => ({ ...section, order: index }))
}

function makeId(prefix = 'section') {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`
}

const ORG_ASSET_SLOT_LABELS = {
  backgroundImage: '배경 이미지',
  mainImage: '대표 이미지',
  sideImage: '보조 이미지',
}

function getOrgAssetUsageLabel(asset) {
  const sectionLabel = SECTION_TYPE_LABELS[asset?.sectionType] || '섹션'
  const slotLabel = ORG_ASSET_SLOT_LABELS[asset?.slotKey] || '이미지'
  return `${sectionLabel} / ${slotLabel}`
}

function AssetPickerModal({ open, assets, onClose, onSelect }) {
  const [categoryFilter, setCategoryFilter] = React.useState('ALL')

  React.useEffect(() => {
    if (!open) return
    const hasTemplateSample = assets.some((asset) => asset.assetCategory === 'TEMPLATE_SAMPLE')
    setCategoryFilter(hasTemplateSample ? 'TEMPLATE_SAMPLE' : 'ALL')
  }, [open, assets])

  const filteredAssets = categoryFilter === 'ALL'
    ? assets
    : assets.filter((asset) => String(asset.assetCategory || 'SECTION_LIBRARY') === categoryFilter)

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-5xl rounded-3xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">운영자 제공 이미지 선택</h3>
            <p className="text-sm text-slate-500">기본 템플릿 이미지 또는 운영 라이브러리 이미지를 선택해 이 조직 화면에만 적용할 수 있습니다.</p>
          </div>
          <button type="button" className="rounded-xl border px-4 py-2 text-sm" onClick={onClose}>닫기</button>
        </div>
        <div className="mb-4 flex flex-wrap gap-2">
          <button type="button" className={`rounded-full px-3 py-1.5 text-xs ${categoryFilter === 'ALL' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`} onClick={() => setCategoryFilter('ALL')}>전체</button>
          <button type="button" className={`rounded-full px-3 py-1.5 text-xs ${categoryFilter === 'TEMPLATE_SAMPLE' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`} onClick={() => setCategoryFilter('TEMPLATE_SAMPLE')}>기본 템플릿 이미지</button>
          <button type="button" className={`rounded-full px-3 py-1.5 text-xs ${categoryFilter === 'SECTION_LIBRARY' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`} onClick={() => setCategoryFilter('SECTION_LIBRARY')}>운영 라이브러리 이미지</button>
        </div>
        <div className="grid max-h-[70vh] gap-4 overflow-y-auto md:grid-cols-3">
          {filteredAssets.map((asset) => (
            <button
              type="button"
              key={asset.id}
              onClick={() => onSelect(asset)}
              className="overflow-hidden rounded-2xl border border-slate-200 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="aspect-[4/3] bg-slate-100">
                <img
                  src={getPreviewImageUrl(asset.previewImageFileId || asset.imageFileId)}
                  alt={asset.name}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="space-y-2 p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] font-semibold text-slate-400">{getOrgAssetUsageLabel(asset)}</div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${asset.assetCategory === 'TEMPLATE_SAMPLE' ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {asset.assetCategory === 'TEMPLATE_SAMPLE' ? '기본 템플릿' : '운영 라이브러리'}
                  </span>
                </div>
                <div className="font-semibold text-slate-900">{asset.name}</div>
                {asset.description ? <div className="text-sm leading-6 text-slate-500">{asset.description}</div> : null}
                <div className="flex flex-wrap gap-2">
                  {(asset.tags || []).map((tag) => (
                    <span key={`${asset.id}-${tag}`} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">{tag}</span>
                  ))}
                </div>
              </div>
            </button>
          ))}
          {filteredAssets.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 md:col-span-3">
              선택 가능한 이미지가 없습니다.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function InfoModal({ open, title, description, onClose, children }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 p-4">
      <div className="w-full max-w-3xl rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-bold text-slate-900">{title}</div>
            {description ? <div className="mt-1 text-sm text-slate-500">{description}</div> : null}
          </div>
          <button type="button" className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700" onClick={onClose}>닫기</button>
        </div>
        <div className="mt-5 max-h-[70vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  )
}

function ItemListEditor({ items = [], onChange, itemLabel = '항목', fields = [{ key: 'title', label: '제목' }, { key: 'body', label: '설명', multiline: true }] }) {
  const updateItem = (index, key, value) => {
    onChange(items.map((item, idx) => (idx === index ? { ...item, [key]: value } : item)))
  }
  const removeItem = (index) => {
    onChange(items.filter((_, idx) => idx !== index))
  }
  const moveItem = (index, direction) => {
    const next = [...items]
    const target = direction === 'up' ? index - 1 : index + 1
    if (target < 0 || target >= next.length) return
      ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }
  const addItem = () => {
    const base = {}
    fields.forEach((field) => { base[field.key] = '' })
    onChange([...(items || []), base])
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700">{itemLabel}</div>
        <button type="button" className="rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1 text-sm font-semibold text-teal-800 hover:bg-teal-100" onClick={addItem}>+ 추가</button>
      </div>
      {(items || []).map((item, index) => (
        <div key={`${itemLabel}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{itemLabel} {index + 1}</div>
            <div className="flex items-center gap-2">
              <button type="button" className="rounded-lg border px-2 py-1 text-xs" onClick={() => moveItem(index, 'up')} disabled={index === 0}>위</button>
              <button type="button" className="rounded-lg border px-2 py-1 text-xs" onClick={() => moveItem(index, 'down')} disabled={index === items.length - 1}>아래</button>
              <button type="button" className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-600" onClick={() => removeItem(index)}>삭제</button>
            </div>
          </div>
          <div className="space-y-3">
            {fields.map((field) => field.multiline ? (
              <textarea
                key={`${itemLabel}-${index}-${field.key}`}
                rows={3}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                placeholder={field.label}
                value={item?.[field.key] || ''}
                onChange={(e) => updateItem(index, field.key, e.target.value)}
              />
            ) : (
              <input
                key={`${itemLabel}-${index}-${field.key}`}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                placeholder={field.label}
                value={item?.[field.key] || ''}
                onChange={(e) => updateItem(index, field.key, e.target.value)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default function OrgLandingPageEditor({
  orgId,
  onUpdate,
  selectedOrg,
  templateMode = false,
  templateData = null,
  onTemplateSave = null,
}) {
  const { show } = useToast()
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [templateLoading, setTemplateLoading] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [landingPage, setLandingPage] = React.useState(null)
  const [templateItems, setTemplateItems] = React.useState([])
  const [assetItems, setAssetItems] = React.useState([])
  const [mobileEditorTab, setMobileEditorTab] = React.useState('edit')
  const [previewDeviceMode, setPreviewDeviceMode] = React.useState('pc')
  const [pageState, setPageState] = React.useState(createDefaultPageState)
  const [pickerState, setPickerState] = React.useState({ open: false, sectionId: '', slotKey: '', sectionType: '' })
  const [metrics, setMetrics] = React.useState(null)
  const [historyItems, setHistoryItems] = React.useState([])
  const [draggingSectionId, setDraggingSectionId] = React.useState('')
  const [dragOverSectionId, setDragOverSectionId] = React.useState('')
  const [dragOverPosition, setDragOverPosition] = React.useState('before')
  const [infoModal, setInfoModal] = React.useState('')
  const [sectionPaletteOpen, setSectionPaletteOpen] = React.useState(false)
  const [expandedSectionId, setExpandedSectionId] = React.useState('')
  /** 1 템플릿·기본설정 → 2 섹션 추가 → 3 순서 → 4 섹션별 편집 */
  const [guidedPhase, setGuidedPhase] = React.useState(1)
  const [guidedEditIndex, setGuidedEditIndex] = React.useState(0)
  /** STEP3: 선택한 섹션 — 왼쪽 미리보기 스크롤 동기화 */
  const [guidedOrderFocusId, setGuidedOrderFocusId] = React.useState('')
  const [reconfigureOpen, setReconfigureOpen] = React.useState(false)
  const [reconfigurePurpose, setReconfigurePurpose] = React.useState('recruit')
  const [reconfigureFeatures, setReconfigureFeatures] = React.useState(() => getDefaultOrgHomepageFeatureValues(createDefaultPageState(), 'recruit'))
  const [initialSetupDone, setInitialSetupDone] = React.useState(false)
  const [orgEditorMode, setOrgEditorMode] = React.useState('guided')

  React.useEffect(() => {
    const loadTemplates = async () => {
      setTemplateLoading(true)
      try {
        const [templateRes, assetRes] = await Promise.all([
          api.get('/org-landing-page-templates', { params: { active: true } }),
          api.get('/org-landing-section-assets', { params: { active: true } }),
        ])
        const templatePayload = templateRes?.data?.data || templateRes?.data
        const assetPayload = assetRes?.data?.data || assetRes?.data
        setTemplateItems(Array.isArray(templatePayload?.items) ? templatePayload.items : [])
        const apiAssetItems = Array.isArray(assetPayload?.items) ? assetPayload.items : []
        setAssetItems([...BUILTIN_ORG_HOMEPAGE_IMAGE_ASSETS, ...apiAssetItems])
      } catch {
        setTemplateItems([])
        setAssetItems(BUILTIN_ORG_HOMEPAGE_IMAGE_ASSETS)
      } finally {
        setTemplateLoading(false)
      }
    }
    loadTemplates()
  }, [])

  React.useEffect(() => {
    if (!templateMode && orgId) {
      ; (async () => {
        setLoading(true)
        setError(null)
        try {
          const res = await api.get(`/orgs/${orgId}/landing-page`)
          const data = res?.data?.data || res?.data
          setLandingPage(data || null)
          setPageState(normalizeLandingPageData(data || {}))
          setInitialSetupDone(false)
        } catch (err) {
          if (err?.response?.status === 404) {
            setLandingPage(null)
            setPageState(createDefaultPageState())
            setInitialSetupDone(false)
          } else {
            setError('홈페이지 정보를 불러오지 못했습니다.')
          }
        } finally {
          setLoading(false)
        }
      })()
    }
  }, [orgId, templateMode])

  React.useEffect(() => {
    if (!templateMode) return
    setLandingPage(null)
    setPageState(normalizeLandingPageData(templateData || createDefaultPageState()))
  }, [templateMode, templateData])

  const loadOperationalData = React.useCallback(async () => {
    if (templateMode || !orgId) return
    try {
      const [metricsRes, historyRes] = await Promise.all([
        api.get(`/orgs/${orgId}/landing-page/metrics`, { __silent: true }),
        api.get(`/orgs/${orgId}/landing-page/history`, { __silent: true }),
      ])
      setMetrics(metricsRes?.data?.data || metricsRes?.data || null)
      const historyPayload = historyRes?.data?.data || historyRes?.data || {}
      setHistoryItems(Array.isArray(historyPayload.items) ? historyPayload.items : [])
    } catch {
      setMetrics(null)
      setHistoryItems([])
    }
  }, [orgId, templateMode])

  React.useEffect(() => {
    loadOperationalData()
  }, [loadOperationalData])

  React.useEffect(() => {
    setExpandedSectionId((prev) => (
      prev && pageState.sections.some((section) => section.id === prev)
        ? prev
        : ''
    ))
  }, [pageState.sections])

  React.useEffect(() => {
    setGuidedEditIndex((idx) => {
      const max = Math.max(0, pageState.sections.length - 1)
      return Math.min(Math.max(0, idx), max)
    })
  }, [pageState.sections.length])

  React.useEffect(() => {
    if (guidedPhase === 2) setSectionPaletteOpen(true)
  }, [guidedPhase])

  React.useEffect(() => {
    if (guidedPhase !== 3) return
    setGuidedOrderFocusId((prev) => {
      if (prev && pageState.sections.some((s) => s.id === prev)) return prev
      return pageState.sections[0]?.id || ''
    })
  }, [guidedPhase, pageState.sections])

  React.useEffect(() => {
    if (guidedPhase !== 4) return
    const sec = pageState.sections[guidedEditIndex]
    if (sec?.id) setExpandedSectionId(sec.id)
  }, [guidedPhase, guidedEditIndex, pageState.sections.length])

  const updatePage = (updater) => {
    setPageState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      return {
        ...prev,
        sections: sortSections(next.sections || []),
      }
    })
  }

  const updateSection = (sectionId, updater) => {
    updatePage((prev) => ({
      ...prev,
      sections: prev.sections.map((section) => {
        if (section.id !== sectionId) return section
        return typeof updater === 'function' ? updater(section) : { ...section, ...updater }
      }),
    }))
  }

  const addSection = (type, templateCode) => {
    const meta = getSectionMeta(type)
    const currentCount = pageState.sections.filter((section) => section.type === type).length
    if (meta?.maxCount && currentCount >= meta.maxCount) {
      show(`${meta.label} 섹션은 최대 ${meta.maxCount}개까지 추가할 수 있습니다.`, 'error')
      return
    }
    const nextSection = {
      id: makeId(type),
      order: pageState.sections.length,
      ...createSectionDraft(type, templateCode),
    }
    updatePage((prev) => ({
      ...prev,
      sections: [...prev.sections, nextSection],
    }))
    setExpandedSectionId(nextSection.id)
  }

  const removeSection = (sectionId) => {
    updatePage((prev) => ({
      ...prev,
      sections: prev.sections.filter((section) => section.id !== sectionId),
    }))
  }

  const moveSection = (sectionId, direction) => {
    const next = [...pageState.sections]
    const index = next.findIndex((section) => section.id === sectionId)
    if (index < 0) return
    const target = direction === 'up' ? index - 1 : index + 1
    if (target < 0 || target >= next.length) return
      ;[next[index], next[target]] = [next[target], next[index]]
    updatePage((prev) => ({ ...prev, sections: withSequentialSectionOrder(next) }))
  }

  const reorderSection = (draggedSectionId, targetSectionId, position = 'before') => {
    if (!draggedSectionId || !targetSectionId || draggedSectionId === targetSectionId) return
    const next = [...pageState.sections]
    const fromIndex = next.findIndex((section) => section.id === draggedSectionId)
    const toIndex = next.findIndex((section) => section.id === targetSectionId)
    if (fromIndex < 0 || toIndex < 0) return
    const [moved] = next.splice(fromIndex, 1)
    const adjustedIndex = fromIndex < toIndex ? toIndex - 1 : toIndex
    const insertIndex = position === 'after' ? adjustedIndex + 1 : adjustedIndex
    next.splice(insertIndex, 0, moved)
    updatePage((prev) => ({ ...prev, sections: withSequentialSectionOrder(next) }))
  }

  const duplicateSection = (sectionId) => {
    const target = pageState.sections.find((section) => section.id === sectionId)
    if (!target) return
    const meta = getSectionMeta(target.type)
    const currentCount = pageState.sections.filter((section) => section.type === target.type).length
    if (meta?.maxCount && currentCount >= meta.maxCount) {
      show(`${meta.label} 섹션은 최대 ${meta.maxCount}개까지 추가할 수 있습니다.`, 'error')
      return
    }
    const clonedSection = {
      ...cloneSection(target, makeId(target.type)),
      order: pageState.sections.length,
    }
    updatePage((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        clonedSection,
      ],
    }))
    setExpandedSectionId(clonedSection.id)
  }

  const applyTemplate = (templateId) => {
    const selected = (templateItems || []).find((item) => String(item.id) === String(templateId))
    if (!selected?.preset) return
    setPageState((prev) => ({
      ...normalizeLandingPageData(selected.preset || {}),
      templateId: selected.code || prev.templateId || 'default',
      meta: selected.preset?.meta || prev.meta,
    }))
    show(`템플릿 "${selected.name}"이 적용되었습니다.`, 'success')
  }

  const applySuggestedCopy = (section) => {
    if (!section) return
    const suggested = createSectionDraft(section.type, section.templateCode)
    updateSection(section.id, (prev) => ({
      ...prev,
      content: {
        ...(suggested.content || {}),
        navLabel: prev.content?.navLabel || suggested.content?.navLabel || '',
      },
      assets: {
        ...(Object.keys(prev.assets || {}).length > 0 ? prev.assets : (suggested.assets || {})),
      },
    }))
    show('추천 문구를 적용했습니다. 필요하면 바로 수정해 사용하세요.', 'success')
  }

  const toggleSectionExpanded = (sectionId) => {
    setExpandedSectionId((prev) => (prev === sectionId ? '' : sectionId))
  }

  const uploadImage = async (file) => {
    if (!file) return ''
    if (file.size > 10 * 1024 * 1024) {
      show('이미지 크기는 10MB 이하여야 합니다.', 'error')
      return ''
    }
    const body = new FormData()
    body.append('file', file)
    const res = await api.post('/files/upload/public-landing-image', body)
    return res?.data?.data?.fileId || res?.data?.fileId || ''
  }

  const onUploadLogo = async (file) => {
    try {
      const fileId = await uploadImage(file)
      if (!fileId) return
      setPageState((prev) => ({ ...prev, logoFileId: fileId }))
      show('로고가 업로드되었습니다.', 'success')
    } catch (err) {
      show(err?.response?.data?.message || err?.message || '로고 업로드 실패', 'error')
    }
  }

  const onUploadSectionAsset = async (sectionId, slotKey, file) => {
    try {
      const fileId = await uploadImage(file)
      if (!fileId) return
      updateSection(sectionId, (section) => ({
        ...section,
        assets: {
          ...(section.assets || {}),
          [slotKey]: {
            sourceType: 'upload',
            fileId,
          },
        },
      }))
      show('이미지가 업로드되었습니다.', 'success')
    } catch (err) {
      show(err?.response?.data?.message || err?.message || '이미지 업로드 실패', 'error')
    }
  }

  const buildPayload = () => {
    const sections = sortSections(pageState.sections)
    const navigationMenu = buildNavigationFromSections(sections)
    return {
      templateId: pageState.templateId || 'default',
      meta: pageState.meta || {},
      style: pageState.style || {},
      logoFileId: pageState.logoFileId || '',
      sections,
      navigationMenu,
    }
  }

  const handleSave = async (e) => {
    e?.preventDefault?.()
    setSaving(true)
    try {
      const payload = buildPayload()
      if (templateMode) {
        await onTemplateSave?.(payload)
      } else {
        await api.put(`/orgs/${orgId}/landing-page`, payload)
        const res = await api.get(`/orgs/${orgId}/landing-page`)
        const data = res?.data?.data || res?.data
        setLandingPage(data || null)
        setPageState(normalizeLandingPageData(data || payload))
        onUpdate?.()
        loadOperationalData()
      }
      show(templateMode ? '템플릿 내용이 저장되었습니다.' : '조직 홈페이지 초안이 저장되었습니다.', 'success')
    } catch (err) {
      show(err?.response?.data?.message || err?.message || '저장에 실패했습니다.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async (isPublished) => {
    if (!orgId) return
    try {
      await api.patch(`/orgs/${orgId}/landing-page/publish`, { isPublished })
      const res = await api.get(`/orgs/${orgId}/landing-page`)
      const data = res?.data?.data || res?.data
      setLandingPage(data || null)
      if (data) {
        setPageState((prev) => ({
          ...prev,
          isPublished: Boolean(data.isPublished),
        }))
      }
      loadOperationalData()
      show(isPublished ? '현재 초안이 공개 버전으로 반영되었습니다.' : '공개가 중지되었습니다.', 'success')
      onUpdate?.()
    } catch (err) {
      show(err?.response?.data?.message || err?.message || '발행 상태 변경에 실패했습니다.', 'error')
    }
  }

  const copyToClipboard = React.useCallback(async (text, successMessage = '복사되었습니다.') => {
    if (!text) {
      show('복사할 링크가 없습니다.', 'error')
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      show(successMessage, 'success')
    } catch {
      show('클립보드 복사에 실패했습니다.', 'error')
    }
  }, [show])

  const openReconfigure = React.useCallback(() => {
    const purpose = getOrgHomepagePurposeFromState(pageState)
    setReconfigurePurpose(purpose)
    setReconfigureFeatures(getDefaultOrgHomepageFeatureValues(pageState, purpose))
    setReconfigureOpen(true)
  }, [pageState])

  const reconfigurePreviewState = React.useMemo(() => buildOrgHomepageReconfigureState({
    currentPageState: pageState,
    purposeId: reconfigurePurpose,
    features: reconfigureFeatures,
  }), [pageState, reconfigureFeatures, reconfigurePurpose])

  const reconfigureWritingItems = React.useMemo(
    () => getOrgWritingItems(reconfigurePreviewState),
    [reconfigurePreviewState],
  )

  const reconfigureSelectedFeatureLabels = React.useMemo(
    () => ORG_HOMEPAGE_RECONFIGURE_FEATURES
      .filter((feature) => feature.required || reconfigureFeatures[feature.id] === true)
      .map((feature) => feature.label),
    [reconfigureFeatures],
  )

  const applyReconfigure = React.useCallback(() => {
    const next = reconfigurePreviewState
    setPageState(next)
    setGuidedPhase(4)
    setGuidedEditIndex(0)
    setGuidedOrderFocusId(next.sections?.[0]?.id || '')
    setExpandedSectionId(next.sections?.[0]?.id || '')
    setMobileEditorTab('edit')
    setOrgEditorMode('guided')
    setInitialSetupDone(true)
    setReconfigureOpen(false)
    show('조직홈페이지 구성을 적용했습니다. 저장하면 초안에 반영됩니다.', 'success')
  }, [reconfigurePreviewState, show])

  const browserOrigin = typeof window !== 'undefined' ? window.location.origin : ''
  const publicLandingUrl = !templateMode && orgId ? `${browserOrigin}/orgs/${orgId}/landing` : ''
  const orgOgVersion = landingPage?.ogVersion || landingPage?.publishedAt || landingPage?.updatedAt || ''
  const sharedLandingUrl = !templateMode && orgId ? orgLandingOgShareUrl(orgId, orgOgVersion) : ''
  const shareButtonsDisabled = !landingPage?.isPublished
  const canShare = !templateMode && Boolean(landingPage?.isPublished && publicLandingUrl && sharedLandingUrl)
  const utilityButtonClass = 'inline-flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50'
  const primaryButtonClass = 'inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-white disabled:opacity-60'
  const shareActionBtnClass =
    'inline-flex min-h-[36px] min-w-[4.5rem] flex-1 items-center justify-center whitespace-nowrap rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 sm:min-w-0 sm:flex-none sm:px-3 sm:text-sm'
  const sectionMetaPillClass = 'rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-500'
  const sectionActionButtonClass = 'inline-flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50'
  const sectionDangerButtonClass = 'inline-flex h-9 items-center justify-center rounded-xl border border-red-200 bg-red-50 px-3 text-xs font-semibold text-red-600 transition hover:bg-red-100'
  /** 3/4 순서 단계 카드 내 미니 버튼 */
  const orderStepActionButtonClass = 'inline-flex h-8 min-w-[2rem] shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-2 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-50'
  const orderStepDangerButtonClass = 'inline-flex h-8 min-w-[2rem] shrink-0 items-center justify-center rounded-lg border border-red-200 bg-red-50 px-2 text-[11px] font-semibold text-red-600 transition hover:bg-red-100'
  const basicSettingsSummary = [
    pageState.meta?.title ? `SEO 제목 설정됨` : 'SEO 제목 미설정',
    pageState.style?.primaryColor ? `대표색 ${pageState.style.primaryColor}` : '대표색 미설정',
    pageState.logoFileId ? '로고 업로드됨' : '로고 미업로드',
    !templateMode && pageState.templateId ? `템플릿 ${pageState.templateId}` : null,
  ].filter(Boolean)
  const sectionPaletteVisible = sectionPaletteOpen || pageState.sections.length === 0
  const smsShareHref = React.useMemo(() => {
    if (!canShare) return ''
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
    const sep = /iPhone|iPad|iPod/i.test(ua) ? '&' : '?'
    const orgName = selectedOrg?.name || '조직홈페이지'
    return `sms:${sep}body=${encodeURIComponent(`${orgName} 조직홈페이지: ${sharedLandingUrl}`)}`
  }, [canShare, selectedOrg?.name, sharedLandingUrl])

  const handleKakaoShare = React.useCallback(async () => {
    if (!canShare) return
    try {
      initKakao()
      const Kakao = window.Kakao
      if (!Kakao?.Share?.sendScrap) throw new Error('kakao_share_not_supported')
      Kakao.Share.sendScrap({ requestUrl: sharedLandingUrl })
    } catch {
      await copyToClipboard(sharedLandingUrl, '카카오 공유용 링크가 복사되었습니다.')
    }
  }, [canShare, copyToClipboard, sharedLandingUrl])

  const orgPreviewDraft = React.useMemo(() => normalizeLandingPageData(pageState), [pageState])
  const orgPreviewName = selectedOrg?.name || '조직'

  const filteredAssets = assetItems.filter((asset) => (
    asset.sectionType === pickerState.sectionType && asset.slotKey === pickerState.slotKey
  ))

  if (loading) return <Loading />
  if (error) return <InlineError message={error} />

  const reconfigureContentProps = {
    title: '조직홈페이지 다시 구성하기',
    description: '조직명과 기본 정보는 유지하고, 목적에 맞는 섹션 구성과 순서를 다시 정리합니다.',
    purposeLabel: '조직홈페이지의 목적은 무엇인가요?',
    purposes: ORG_HOMEPAGE_RECONFIGURE_PURPOSES,
    purposeId: reconfigurePurpose,
    onPurposeIdChange: (value) => {
      setReconfigurePurpose(value)
      setReconfigureFeatures(getDefaultOrgHomepageFeatureValues(pageState, value))
    },
    features: ORG_HOMEPAGE_RECONFIGURE_FEATURES,
    featureValues: reconfigureFeatures,
    onFeatureChange: (id, value) => setReconfigureFeatures((prev) => ({ ...prev, [id]: value })),
    onApply: applyReconfigure,
    applyLabel: '작성 시작',
    note: '적용 후 바로 공개되지는 않습니다. 미리보기로 확인한 뒤 초안 저장 또는 공개 발행을 진행하세요.',
    stepMode: true,
    stepLabels: ['목적 선택', '기능 선택', '작성 항목'],
    renderReview: () => (
      <section>
        <div className="text-sm font-bold text-slate-900">이 순서로 작성합니다</div>
        <div className="mt-3 overflow-hidden rounded-3xl border border-slate-200 bg-white">
          {reconfigureWritingItems.map((item, index) => (
            <div
              key={item.id}
              className={`flex gap-3 px-4 py-4 ${index === 0 ? '' : 'border-t border-slate-100'}`}
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-50 text-xs font-black text-brand-navy">
                {index + 1}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-extrabold text-slate-950">{item.label}</div>
                <div className="mt-1 text-xs leading-5 text-slate-500">{item.description}</div>
              </div>
            </div>
          ))}
        </div>
        {reconfigureSelectedFeatureLabels.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {reconfigureSelectedFeatureLabels.map((label) => (
              <span
                key={label}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700"
              >
                {label}
              </span>
            ))}
          </div>
        ) : null}
      </section>
    ),
  }

  const reconfigureModal = (
    <EasyReconfigureModal
      open={reconfigureOpen}
      {...reconfigureContentProps}
      onClose={() => setReconfigureOpen(false)}
    />
  )

  if (!templateMode && !landingPage && !initialSetupDone) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 py-10">
          <EasyReconfigureContent
            {...reconfigureContentProps}
            title="생성된 조직홈페이지가 없습니다. 만들어주세요."
            description="질문에 답하면 상단메뉴, 히어로, 조직소개 순서가 잡힌 기본 구성이 만들어집니다."
            showCloseButton={false}
            className="shadow-sm"
          />
        </div>
      </div>
    )
  }

  const renderAssetEditor = (section, slotKey, label) => {
    const value = section.assets?.[slotKey] || {}
    const fileId = value.fileId || ''
    const sizeGuide = getOrgSectionAssetImageGuide(section.type, slotKey)
    return (
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-col gap-2">
          <div className="text-sm font-semibold text-slate-700">{label}</div>
          {sizeGuide ? (
            <div className="rounded-xl bg-white/80 px-3 py-2 text-[11px] leading-relaxed text-slate-600 ring-1 ring-slate-200/80">
              {sizeGuide}
            </div>
          ) : null}
          <button
            type="button"
            className={`${sectionActionButtonClass} w-full justify-center`}
            onClick={() => setPickerState({ open: true, sectionId: section.id, slotKey, sectionType: section.type })}
          >
            기본/운영 이미지 선택
          </button>
          <label className={`${sectionActionButtonClass} flex w-full cursor-pointer justify-center`}>
            이 조직 이미지 업로드
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => onUploadSectionAsset(section.id, slotKey, e.target.files?.[0])}
            />
          </label>
        </div>
        {fileId ? (
          <div className="space-y-2">
            <img src={getPreviewImageUrl(fileId)} alt={label} className="h-40 w-full rounded-2xl object-cover" />
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{value.sourceType === 'library' ? '공통 이미지에서 선택됨' : '이 조직 전용 업로드'}</span>
              {value.sourceType === 'library' && value.assetCategory ? (
                <span className="ml-2 rounded-full bg-slate-100 px-2 py-1 text-[11px]">
                  {value.assetCategory === 'TEMPLATE_SAMPLE' ? '기본 템플릿' : '운영 라이브러리'}
                </span>
              ) : null}
              <button
                type="button"
                className="text-red-600 hover:underline"
                onClick={() => updateSection(section.id, (target) => ({
                  ...target,
                  assets: { ...(target.assets || {}), [slotKey]: {} },
                }))}
              >
                제거
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-400">
            이미지가 선택되지 않았습니다.
          </div>
        )}
      </div>
    )
  }

  const renderYoutubeEditor = (section) => (
    <div className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div>
        <div className="text-sm font-semibold text-slate-700">유튜브 링크</div>
      </div>
      <input
        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
        placeholder="https://www.youtube.com/watch?v=..."
        value={section.content?.youtubeUrl || ''}
        onChange={(e) => updateSection(section.id, (prev) => ({
          ...prev,
          content: { ...(prev.content || {}), youtubeUrl: e.target.value },
        }))}
      />
    </div>
  )

  const renderSectionEditor = (section, opts = {}) => {
    const variant = opts.variant || 'full'
    const orderOnly = variant === 'orderOnly'
    const content = section.content || {}
    const updateContent = (key, value) => updateSection(section.id, (prev) => ({
      ...prev,
      content: { ...(prev.content || {}), [key]: value },
    }))
    const meta = getSectionMeta(section.type)
    const currentIndex = pageState.sections.findIndex((item) => item.id === section.id)
    const isDragging = draggingSectionId === section.id
    const isDragTarget = dragOverSectionId === section.id && draggingSectionId && draggingSectionId !== section.id
    const isExpanded = orderOnly ? false : expandedSectionId === section.id
    const orderStepSelected = Boolean(opts.orderStepSelected)
    const activeTemplateLabel = !orderOnly
      ? ((meta?.templates || []).find((item) => item.code === section.templateCode)?.label || section.templateCode)
      : ''
    return (
      <div
        key={section.id}
        className={`relative border bg-white transition-all ${orderOnly ? 'rounded-xl shadow-none' : 'rounded-3xl shadow-sm'
          } ${isDragging ? 'scale-[0.995] opacity-60 shadow-none' : ''
          } ${isDragTarget ? 'border-blue-400 bg-blue-50/20 ring-2 ring-blue-100' : 'border-slate-200'
          } ${orderOnly && orderStepSelected ? 'ring-2 ring-brand-navy/30 ring-offset-1 ring-offset-white' : ''
          }`}
        onDragOver={(e) => {
          if (!draggingSectionId) return
          e.preventDefault()
          const nextPosition = resolveDropPosition(e)
          if (dragOverSectionId !== section.id) setDragOverSectionId(section.id)
          if (dragOverPosition !== nextPosition) setDragOverPosition(nextPosition)
        }}
        onDrop={(e) => {
          e.preventDefault()
          reorderSection(draggingSectionId, section.id, dragOverPosition)
          setDraggingSectionId('')
          setDragOverSectionId('')
          setDragOverPosition('before')
        }}
        onDragEnd={() => {
          setDraggingSectionId('')
          setDragOverSectionId('')
          setDragOverPosition('before')
        }}
      >
        {isDragTarget ? (
          <div
            className={`pointer-events-none absolute inset-x-4 z-10 flex items-center gap-2 ${dragOverPosition === 'after' ? 'bottom-0 translate-y-1/2' : 'top-0 -translate-y-1/2'
              }`}
          >
            <div className="h-[3px] flex-1 rounded-full bg-brand-navy shadow-[0_0_0_3px_rgba(15,23,42,0.12)]" />
            <div className="shrink-0 rounded-full bg-brand-navy px-3 py-1 text-[11px] font-semibold text-white shadow-sm">
              {dragOverPosition === 'after' ? '아래로 이동' : '위로 이동'}
            </div>
            <div className="h-[3px] flex-1 rounded-full bg-brand-navy shadow-[0_0_0_3px_rgba(15,23,42,0.12)]" />
          </div>
        ) : null}
        {orderOnly ? (
          <div className="space-y-2 px-3 py-2">
            <div className="flex items-center gap-2">
              <div
                data-order-action
                draggable
                onDragStart={(e) => {
                  setDraggingSectionId(section.id)
                  setDragOverSectionId(section.id)
                  try {
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', section.id)
                  } catch {
                    // noop
                  }
                }}
                className="select-none shrink-0"
              >
                <SectionOrderHandle currentIndex={currentIndex} dragging={isDragging} compact showIndex={false} />
              </div>
              <button
                type="button"
                className="min-w-0 flex-1 rounded-lg px-1 py-0.5 text-left hover:bg-slate-50/90"
                onClick={() => setGuidedOrderFocusId(section.id)}
              >
                <span className="text-sm font-semibold text-slate-900">
                  <span className="tabular-nums font-semibold text-slate-500">{currentIndex + 1}</span>
                  {' '}
                  {meta?.label || section.type}
                </span>
                {isDragging ? (
                  <span className="ml-2 text-[11px] text-slate-400">이동 중…</span>
                ) : null}
              </button>
            </div>
            <div data-order-action className="flex flex-wrap items-center gap-1">
              <button type="button" className={orderStepActionButtonClass} onClick={() => moveSection(section.id, 'up')}>위</button>
              <button type="button" className={orderStepActionButtonClass} onClick={() => moveSection(section.id, 'down')}>아래</button>
              <button type="button" className={orderStepActionButtonClass} onClick={() => duplicateSection(section.id)}>복제</button>
              <button type="button" className={orderStepDangerButtonClass} onClick={() => removeSection(section.id)}>삭제</button>
            </div>
          </div>
        ) : (
          <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${isExpanded ? 'border-b border-slate-100 bg-slate-50/70' : ''}`}>
            <div className="flex items-start gap-3">
              <div
                draggable
                onDragStart={(e) => {
                  setDraggingSectionId(section.id)
                  setDragOverSectionId(section.id)
                  try {
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', section.id)
                  } catch {
                    // noop
                  }
                }}
                className="select-none"
              >
                <SectionOrderHandle currentIndex={currentIndex} dragging={isDragging} />
              </div>
              <div className="pt-0.5">
                <div className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{SECTION_TYPE_LABELS[section.type] || section.type}</div>
                {/* <div className="mt-1 text-[11px] text-slate-400">
                  {isDragging ? '이동 중...' : '드래그 또는 위/아래 버튼으로 순서 변경'}
                </div> */}
              </div>
            </div>
            {/* <button
              type="button"
              className="min-w-0 flex-1 text-left disabled:cursor-default"
              onClick={() => { toggleSectionExpanded(section.id) }}
            >
              <div className="mt-1 text-lg font-bold text-slate-900">{content.title || meta?.label || section.type}</div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                <span className={sectionMetaPillClass}>템플릿 {activeTemplateLabel || '-'}</span>
                <span className={section.enabled !== false ? 'rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700' : 'rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-500'}>
                  {section.enabled !== false ? '표시 중' : '숨김'}
                </span>
                {!isExpanded ? (
                  <span className={sectionMetaPillClass}>클릭해서 상세 편집</span>
                ) : null}
              </div>
            </button> */}
            <div className="flex flex-wrap items-center gap-2">
              {/* <>
                <button
                  type="button"
                  className={sectionActionButtonClass}
                  onClick={() => toggleSectionExpanded(section.id)}
                >
                  {isExpanded ? '접기' : '펼치기'}
                </button>
                <label className="inline-flex h-9 items-center gap-2 rounded-xl bg-slate-100 px-3 text-xs font-medium text-slate-600">
                  <input
                    type="checkbox"
                    checked={section.enabled !== false}
                    onChange={(e) => updateSection(section.id, { enabled: e.target.checked })}
                  />
                  표시
                </label>
              </> */}
              {guidedPhase !== 4 ? (
                <>
                  <button type="button" className={sectionActionButtonClass} onClick={() => moveSection(section.id, 'up')}>위</button>
                  <button type="button" className={sectionActionButtonClass} onClick={() => moveSection(section.id, 'down')}>아래</button>
                  <button type="button" className={sectionActionButtonClass} onClick={() => duplicateSection(section.id)}>복제</button>
                  <button type="button" className={sectionDangerButtonClass} onClick={() => removeSection(section.id)}>삭제</button>
                </>
              ) : null}
            </div>
          </div>
        )}
        {isExpanded ? (
          <div className="space-y-4 px-5 py-5">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">섹션 템플릿</label>
              <select
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                value={section.templateCode}
                onChange={(e) => updateSection(section.id, (prev) => updateSectionTemplate(prev, e.target.value))}
              >
                {(meta?.templates || []).map((item) => (
                  <option key={item.code} value={item.code}>{item.label}</option>
                ))}
              </select>
              <div className="mt-2 flex items-center justify-between gap-3">
                <button
                  type="button"
                  className="shrink-0 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 text-xs font-semibold text-teal-800 hover:bg-teal-100"
                  onClick={() => applySuggestedCopy(section)}
                >
                  추천 문구 자동 적용
                </button>
              </div>
            </div>

            {['hero', 'ga-intro', 'ceo-message', 'business-intro', 'benefits', 'stats', 'lead-form', 'contact'].includes(section.type) ? (
              <div>
                <textarea
                  rows={section.type === 'hero' ? 4 : 3}
                  className="w-full min-h-[3.5rem] rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  placeholder="섹션 제목"
                  value={content.title || ''}
                  onChange={(e) => updateContent('title', e.target.value)}
                />
                <div className="mt-1 text-[11px] text-slate-500">엔터로 줄바꿈이 가능합니다.</div>
              </div>
            ) : null}

            {['hero', 'ga-intro', 'ceo-message', 'business-intro', 'benefits', 'lead-form'].includes(section.type) ? (
              <textarea
                rows={3}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                placeholder={section.type === 'hero' ? '부제목' : '보조 설명'}
                value={section.type === 'hero' ? (content.subtitle || '') : (content.subtitle || content.body || '')}
                onChange={(e) => {
                  if (section.type === 'hero') updateContent('subtitle', e.target.value)
                  else if (section.type === 'business-intro' || section.type === 'ga-intro' || section.type === 'ceo-message') updateContent('body', e.target.value)
                  else updateContent('subtitle', e.target.value)
                }}
              />
            ) : null}

            {section.type === 'hero' ? (
              <div className="grid gap-4 md:grid-cols-2">
                <input
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  placeholder="버튼 문구"
                  value={content.ctaText || ''}
                  onChange={(e) => updateContent('ctaText', e.target.value)}
                />
                <input
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  placeholder="버튼 링크 (#lead-form 또는 URL)"
                  value={content.ctaLink || ''}
                  onChange={(e) => updateContent('ctaLink', e.target.value)}
                />
                <div className="md:col-span-2">
                  <textarea
                    rows={2}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                    placeholder="상단 짧은 라벨(선택)"
                    value={content.eyebrow || ''}
                    onChange={(e) => updateContent('eyebrow', e.target.value)}
                  />
                </div>
              </div>
            ) : null}

            {section.type === 'ga-intro' || section.type === 'ceo-message' ? (
              <div>
                <textarea
                  rows={2}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  placeholder={section.type === 'ceo-message' ? '직책/보조 문구' : '보조 제목'}
                  value={content.subtitle || ''}
                  onChange={(e) => updateContent('subtitle', e.target.value)}
                />
                {/* <div className="mt-1 text-[11px] text-slate-500">엔터로 줄바꿈 가능합니다.</div> */}
              </div>
            ) : null}

            {section.type === 'business-intro' && section.templateCode === 'business-intro.cards-v1' ? (
              <ItemListEditor
                items={Array.isArray(content.items) ? content.items : []}
                onChange={(items) => updateContent('items', items)}
                itemLabel="사업단 소개 항목"
                fields={[{ key: 'title', label: '제목' }, { key: 'body', label: '설명', multiline: true }]}
              />
            ) : null}

            {section.type === 'benefits' ? (
              <ItemListEditor
                items={Array.isArray(content.items) ? content.items : []}
                onChange={(items) => updateContent('items', items)}
                itemLabel="혜택"
                fields={[{ key: 'title', label: '혜택명' }, { key: 'body', label: '설명', multiline: true }]}
              />
            ) : null}

            {section.type === 'stats' ? (
              <ItemListEditor
                items={Array.isArray(content.items) ? content.items : []}
                onChange={(items) => updateContent('items', items)}
                itemLabel="지표"
                fields={[{ key: 'label', label: '라벨' }, { key: 'value', label: '값' }]}
              />
            ) : null}

            {section.type === 'lead-form' ? (
              <div className="space-y-4">
                <input
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  placeholder="제출 버튼 문구"
                  value={content.submitText || ''}
                  onChange={(e) => updateContent('submitText', e.target.value)}
                />
                <textarea
                  rows={2}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  placeholder="제출 완료 안내 문구"
                  value={content.successMessage || ''}
                  onChange={(e) => updateContent('successMessage', e.target.value)}
                />
              </div>
            ) : null}

            {section.type === 'contact' ? (
              <div className="grid gap-4 md:grid-cols-2">
                <input className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm md:col-span-2" placeholder="주소" value={content.address || ''} onChange={(e) => updateContent('address', e.target.value)} />
                <input className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm" placeholder="전화번호" value={content.phone || ''} onChange={(e) => updateContent('phone', e.target.value)} />
                <input className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm" placeholder="이메일" value={content.email || ''} onChange={(e) => updateContent('email', e.target.value)} />
                <input className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm md:col-span-2" placeholder="카카오톡 링크" value={content.kakaoLink || ''} onChange={(e) => updateContent('kakaoLink', e.target.value)} />
              </div>
            ) : null}

            {['hero', 'ga-intro', 'ceo-message', 'business-intro', 'benefits', 'stats', 'lead-form', 'contact'].includes(section.type)
              ? renderYoutubeEditor(section)
              : null}

            {section.type === 'hero' ? renderAssetEditor(section, 'backgroundImage', '배경 이미지') : null}
            {section.type === 'ga-intro' || section.type === 'ceo-message' || section.type === 'business-intro'
              ? renderAssetEditor(section, 'mainImage', '대표 이미지')
              : null}
            {section.type === 'lead-form' ? renderAssetEditor(section, 'sideImage', '보조 이미지') : null}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AssetPickerModal
        open={pickerState.open}
        assets={filteredAssets}
        onClose={() => setPickerState({ open: false, sectionId: '', slotKey: '', sectionType: '' })}
        onSelect={(asset) => {
          updateSection(pickerState.sectionId, (section) => ({
            ...section,
            assets: {
              ...(section.assets || {}),
              [pickerState.slotKey]: {
                sourceType: 'library',
                libraryAssetId: asset.id,
                assetCategory: asset.assetCategory || 'SECTION_LIBRARY',
                fileId: asset.imageFileId,
              },
            },
          }))
          setPickerState({ open: false, sectionId: '', slotKey: '', sectionType: '' })
        }}
      />
      <InfoModal
        open={infoModal === 'metrics'}
        title="운영 지표"
        onClose={() => setInfoModal('')}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <MetricCard
            label="공개 조회수"
            value={metrics?.viewCount ?? 0}
          />
          <MetricCard
            label="누적 리드"
            value={metrics?.leadCount ?? 0}
          />
          <MetricCard
            label="마지막 공개"
            value={metrics?.publishedAt ? formatDateTime(metrics.publishedAt) : '-'}
          />
          <MetricCard
            label="현재 섹션 수"
            value={metrics?.sectionCount ?? pageState.sections.length}
          />
        </div>
      </InfoModal>
      <InfoModal
        open={infoModal === 'history'}
        title="게시 이력"
        onClose={() => setInfoModal('')}
      >
        <div className="space-y-3">
          {historyItems.map((item) => {
            const tone = item.action === 'PUBLISH'
              ? 'bg-emerald-50 text-emerald-700'
              : item.action === 'UNPUBLISH'
                ? 'bg-amber-50 text-amber-700'
                : 'bg-slate-100 text-slate-700'
            const label = item.action === 'PUBLISH'
              ? '공개 발행'
              : item.action === 'UNPUBLISH'
                ? '공개 중지'
                : '초안 저장'
            return (
              <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${tone}`}>{label}</span>
                    <span className="text-sm font-semibold text-slate-900">{item.summary || label}</span>
                  </div>
                  <span className="text-xs text-slate-500">{formatDateTime(item.createdAt)}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{item.actorEmail || '시스템'}</div>
              </div>
            )
          })}
          {historyItems.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-400">
              아직 기록된 게시 이력이 없습니다.
            </div>
          ) : null}
        </div>
      </InfoModal>
      {reconfigureModal}

      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-[min(100%,90rem)] flex-col gap-3 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold text-slate-900">
                  {templateMode ? '조직 홈페이지 템플릿 편집' : `조직 홈페이지 편집${selectedOrg?.name ? ` · ${selectedOrg.name}` : ''}`}
                </h1>
                <MenuGuideButton slug={templateMode ? 'template-management' : 'org-homepage'} title={templateMode ? '템플릿관리 가이드' : '조직홈페이지 가이드'} />
              </div>
              {!templateMode ? (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${landingPage?.isPublished ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                    }`}>
                    {landingPage?.isPublished ? '공개 중' : '초안'}
                  </span>
                  {landingPage?.hasUnpublishedChanges ? (
                    <span className="rounded-full border border-brand-navy/20 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-brand-navy">
                      미발행 변경 있음
                    </span>
                  ) : null}
                </div>
              ) : null}
              {!templateMode ? (
                <div className="mt-1 text-xs text-slate-500">
                  {landingPage?.publishedAt || landingPage?.publishedAtDisplay
                    ? `마지막 공개 ${formatApiDateTimeDisplay(landingPage.publishedAtDisplay, landingPage.publishedAt)}`
                    : '아직 공개 이력이 없습니다.'}
                </div>
              ) : null}
            </div>

            <div className="flex min-w-0 max-w-full flex-[1_1_16rem] flex-wrap items-center justify-end gap-1.5 sm:gap-2">
              {!templateMode ? (
                <button
                  type="button"
                  className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-800 hover:bg-orange-100"
                  onClick={openReconfigure}
                >
                  다시 처음부터 구성하기
                </button>
              ) : null}
              {!templateMode ? (
                <div className="grid min-w-0 w-full max-w-md flex-[1_1_280px] grid-cols-4 gap-1 sm:w-auto sm:max-w-none sm:flex sm:flex-nowrap">
                  <a
                    className={`${shareActionBtnClass} ${canShare ? '' : 'cursor-not-allowed opacity-40'}`}
                    href={canShare ? publicLandingUrl : undefined}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => { if (!canShare) e.preventDefault() }}
                  >
                    미리보기
                  </a>
                  <button
                    type="button"
                    className={shareActionBtnClass}
                    disabled={!canShare}
                    title="카카오·SNS 미리보기용 OG 링크"
                    onClick={() => {
                      if (canShare) {
                        copyToClipboard(sharedLandingUrl, '카카오·링크 미리보기용(OG) 주소가 복사되었습니다.')
                      }
                    }}
                  >
                    링크복사
                  </button>
                  {/* <button
                    type="button"
                    className={`${shareActionBtnClass} ${canShare ? '' : 'cursor-not-allowed opacity-40'}`}
                    disabled={!canShare}
                    onClick={handleKakaoShare}
                  >
                    카카오톡
                  </button>
                  <a
                    className={`${shareActionBtnClass} ${smsShareHref ? '' : 'cursor-not-allowed opacity-40'}`}
                    href={smsShareHref || undefined}
                    onClick={(e) => { if (!smsShareHref) e.preventDefault() }}
                  >
                    문자
                  </a> */}
                </div>
              ) : null}

              <button
                type="button"
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                onClick={() => navigate(-1)}
              >
                돌아가기
              </button>
              <button type="button" className={`${primaryButtonClass} bg-brand-navy hover:opacity-90`} disabled={saving} onClick={handleSave}>
                {saving ? '저장 중...' : '초안 저장'}
              </button>
              {!templateMode && landingPage ? (
                <>
                  <button
                    type="button"
                    className={`${primaryButtonClass} bg-emerald-600`}
                    onClick={() => handlePublish(true)}
                  >
                    {landingPage.isPublished ? (landingPage.hasUnpublishedChanges ? '초안 재발행' : '다시 발행') : '공개 발행'}
                  </button>
                  {landingPage.isPublished ? (
                    <button
                      type="button"
                      className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-100 px-4 text-sm font-semibold text-slate-700"
                      onClick={() => handlePublish(false)}
                    >
                      공개 중지
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>

          {!templateMode && !canShare ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900">
              공개 발행 후 새 창 미리보기·링크 복사·공유가 활성화됩니다. 왼쪽 「실시간 미리보기」는 저장한 초안 기준으로 표시됩니다.
            </p>
          ) : null}

          {!templateMode ? (
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
              {landingPage ? (
                <ShareLinkActions
                  items={[{ id: 'kakao-share', label: '카카오 공유용(OG)', url: sharedLandingUrl }]}
                  disabled={shareButtonsDisabled}
                  disabledMessage="공개 발행 후 링크를 공유할 수 있습니다."
                  onCopy={copyToClipboard}
                  showCopyButtons={false}
                  qrButtonLabel="QR 보기"
                  buttonClassName="inline-flex h-9 min-w-[88px] items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                />
              ) : null}
              <button type="button" className={utilityButtonClass} onClick={() => setInfoModal('metrics')}>운영 지표</button>
              <button type="button" className={utilityButtonClass} onClick={() => setInfoModal('history')}>게시 이력</button>
              <ComplianceTextCheckButton
                getText={() => collectMarketingStrings(pageState)}
                label="표현 리스크 점검"
                className="inline-flex h-9 items-center justify-center rounded-xl border border-amber-200 bg-amber-50 px-4 text-sm font-semibold text-amber-950 hover:bg-amber-100 disabled:opacity-50"
              />
            </div>
          ) : null}
        </div>
      </header>

      <div className="mx-auto max-w-[min(100%,90rem)] px-4 py-6">
        {!templateMode ? (
          <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-extrabold text-slate-950">편집 방식</div>
                <div className="mt-1 text-xs text-slate-500">
                  {orgEditorMode === 'guided'
                    ? '질문으로 정한 구성을 단계별로 작성합니다.'
                    : '섹션 추가와 상세 편집을 바로 조정합니다.'}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-800 hover:bg-orange-100"
                  onClick={openReconfigure}
                >
                  다시 처음부터 구성하기
                </button>
                <button
                  type="button"
                  className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                    orgEditorMode === 'advanced'
                      ? 'border border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                      : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                  onClick={() => {
                    setOrgEditorMode('advanced')
                    setSectionPaletteOpen(true)
                    setGuidedPhase(pageState.sections.length > 0 ? 4 : 2)
                    setMobileEditorTab('edit')
                  }}
                >
                  고급편집
                </button>
                {orgEditorMode === 'advanced' ? (
                  <button
                    type="button"
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    onClick={() => {
                      setOrgEditorMode('guided')
                      setGuidedPhase(pageState.sections.length > 0 ? 4 : 1)
                      setMobileEditorTab('edit')
                    }}
                  >
                    STEP 편집
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        <div className="mb-4 flex rounded-xl border border-slate-200 bg-white p-1 lg:hidden">
          <button
            type="button"
            className={`min-h-[40px] flex-1 rounded-lg px-3 py-2 text-sm font-semibold ${mobileEditorTab === 'preview' ? 'bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setMobileEditorTab('preview')}
          >
            실시간 미리보기
          </button>
          <button
            type="button"
            className={`min-h-[40px] flex-1 rounded-lg px-3 py-2 text-sm font-semibold ${mobileEditorTab === 'edit' ? 'bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setMobileEditorTab('edit')}
          >
            편집
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[7fr_3fr] lg:items-start">
          <section
            className={`min-w-0 space-y-2 ${mobileEditorTab === 'edit' ? 'hidden' : ''} lg:block`}
            aria-label="실시간 미리보기"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">실시간 미리보기</div>
              <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
                <button
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-[11px] font-semibold transition ${previewDeviceMode === 'pc' ? 'bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'text-slate-600 hover:bg-slate-50'}`}
                  onClick={() => setPreviewDeviceMode('pc')}
                >
                  PC
                </button>
                <button
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-[11px] font-semibold transition ${previewDeviceMode === 'mobile' ? 'bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'text-slate-600 hover:bg-slate-50'}`}
                  onClick={() => setPreviewDeviceMode('mobile')}
                >
                  모바일
                </button>
              </div>
            </div>
            <div className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-5.5rem)] overflow-y-auto rounded-2xl border border-slate-200/90 bg-slate-100/90 p-2 shadow-inner ring-1 ring-black/5">
              <PersonalLandingPreviewViewport mode={previewDeviceMode}>
                <OrgLandingPageViewer
                  orgId={templateMode ? undefined : orgId}
                  landingPageData={orgPreviewDraft}
                  orgName={orgPreviewName}
                  isPreview
                  editorFocusSectionId={guidedPhase === 3 ? guidedOrderFocusId : ''}
                />
              </PersonalLandingPreviewViewport>
            </div>
          </section>

          <section
            className={`min-w-0 space-y-5 ${mobileEditorTab === 'preview' ? 'hidden' : ''} lg:block`}
            aria-label="편집"
          >
            <div className="rounded-2xl border border-slate-200 bg-white p-4 lg:p-5">
              <div className="space-y-6">
                <div className="flex flex-wrap items-center gap-1.5">
                  {[
                    { id: 1, label: '템플릿선택' },
                    { id: 2, label: '섹션선택' },
                    { id: 3, label: '순서' },
                    { id: 4, label: '섹션편집' },
                  ].map((step) => (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => setGuidedPhase(step.id)}
                      className={`rounded-md border px-2.5 py-1 text-xs font-semibold transition ${guidedPhase === step.id
                          ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                          : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
                        }`}
                    >
                      {step.id}/4 {step.label}
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/90 px-2 py-2">
                  <button
                    type="button"
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={guidedPhase <= 1}
                    onClick={() => setGuidedPhase((p) => Math.max(1, p - 1))}
                  >
                    이전
                  </button>
                  <span className="text-[11px] font-medium text-slate-500">{guidedPhase} / 4 단계</span>
                  <button
                    type="button"
                    className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={guidedPhase >= 4}
                    onClick={() => setGuidedPhase((p) => Math.min(4, p + 1))}
                  >
                    다음
                  </button>
                </div>

                {guidedPhase === 1 ? (
                  <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                    <div>
                      <div className="text-xs font-semibold text-slate-900">템플릿 · 페이지 기본 설정</div>
                    </div>
                    {/* <div className="mt-2 flex flex-wrap gap-1">
                      {basicSettingsSummary.map((item) => (
                        <span key={item} className="rounded-full bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200/90">
                          {item}
                        </span>
                      ))}
                    </div> */}

                    <div className="mt-3 space-y-3">
                      {!templateMode ? (
                        <div>
                          {templateLoading ? (
                            <p className="text-[11px] text-slate-500">템플릿 목록을 불러오는 중…</p>
                          ) : null}
                          {(templateItems || []).length > 0 ? (
                            <div>
                              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">템플릿</div>
                              <div className="flex flex-col gap-1.5">
                                {templateItems.map((item) => (
                                  <TemplatePreviewCard
                                    key={item.id}
                                    template={item}
                                    compact
                                    active={String(pageState.templateId || '') === String(item.code || '')}
                                    onSelect={() => applyTemplate(item.id)}
                                  />
                                ))}
                              </div>
                            </div>
                          ) : !templateLoading ? (
                            <p className="text-[11px] text-slate-500">등록된 운영 템플릿이 없습니다.</p>
                          ) : null}
                        </div>
                      ) : null}

                      <div className="space-y-2 border-t border-slate-100 pt-3">
                        <div>
                          <label className="text-[10px] font-medium text-slate-600">SEO 제목</label>
                          <input
                            className="mt-0.5 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
                            placeholder="SEO 제목"
                            value={pageState.meta?.title || ''}
                            onChange={(e) => setPageState((prev) => ({ ...prev, meta: { ...(prev.meta || {}), title: e.target.value } }))}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-medium text-slate-600">SEO 설명</label>
                          <textarea
                            rows={2}
                            className="mt-0.5 w-full resize-y rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
                            placeholder="SEO 설명"
                            value={pageState.meta?.description || ''}
                            onChange={(e) => setPageState((prev) => ({ ...prev, meta: { ...(prev.meta || {}), description: e.target.value } }))}
                          />
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                          <div className="min-w-0 flex-1">
                            <label className="text-[10px] font-medium text-slate-600">대표색</label>
                            <input
                              className="mt-0.5 h-8 w-full max-w-[6rem] cursor-pointer rounded-lg border border-slate-200"
                              type="color"
                              value={pageState.style?.primaryColor || '#0f172a'}
                              onChange={(e) => setPageState((prev) => ({ ...prev, style: { ...(prev.style || {}), primaryColor: e.target.value } }))}
                            />
                          </div>
                          <label className="flex h-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-2.5 text-[11px] font-medium text-slate-700 hover:bg-slate-100">
                            로고
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => onUploadLogo(e.target.files?.[0])} />
                          </label>
                        </div>
                        {pageState.logoFileId ? (
                          <div className="rounded-lg border border-slate-100 bg-slate-50/80 p-2">
                            <img src={getPreviewImageUrl(pageState.logoFileId)} alt="" className="max-h-10 object-contain object-left" />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}

                {guidedPhase === 2 ? (
                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-800">섹션 선택</div>
                        <div className="mt-1 text-xs text-slate-500">페이지에 넣을 섹션을 추가합니다.</div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-500 ring-1 ring-slate-200">
                          현재 {pageState.sections.length}개 · 유형 {SECTION_TEMPLATES.length}종
                        </span>
                        <button
                          type="button"
                          className="inline-flex h-10 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                          onClick={() => setSectionPaletteOpen((prev) => !prev)}
                        >
                          {sectionPaletteVisible ? '목록 접기' : '목록 펼치기'}
                        </button>
                      </div>
                    </div>
                    {sectionPaletteVisible ? (
                      <div className="mt-4 flex flex-col gap-1.5">
                        {SECTION_TEMPLATES.map((meta) => {
                          const currentCount = pageState.sections.filter((section) => section.type === meta.type).length
                          const maxCount = meta.maxCount || null
                          const isAdded = currentCount > 0
                          const isAtLimit = Boolean(maxCount && currentCount >= maxCount)
                          return (
                            <button
                              key={meta.type}
                              type="button"
                              className={`w-full rounded-lg border px-3 py-2.5 text-left text-sm transition ${isAdded
                                  ? 'border-emerald-200 bg-emerald-50/70 hover:border-emerald-300'
                                  : 'border-slate-200 bg-white hover:border-slate-300'
                                }`}
                              onClick={() => addSection(meta.type, meta.templates?.[0]?.code)}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0 font-semibold text-slate-900">{meta.label}</div>
                                <div className="flex shrink-0 items-center gap-1.5">
                                  {isAdded ? (
                                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                      추가됨
                                    </span>
                                  ) : null}
                                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isAtLimit ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
                                    }`}>
                                    {maxCount ? `${currentCount}/${maxCount}` : `${currentCount}`}
                                  </span>
                                </div>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-slate-500">「목록 펼치기」로 섹션 유형을 선택하세요.</p>
                    )}
                  </div>
                ) : null}

                {guidedPhase === 3 ? (
                  <div className="space-y-2">
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <div className="text-xs font-semibold text-slate-800">순서</div>
                      <div className="mt-0.5 text-[11px] leading-snug text-slate-500">드래그 또는 위·아래로 순서를 바꿉니다.문구·이미지는 4단계에서 편집합니다.</div>
                    </div>
                    {pageState.sections.map((section) => renderSectionEditor(section, {
                      variant: 'orderOnly',
                      orderStepSelected: guidedOrderFocusId === section.id,
                    }))}
                    {pageState.sections.length === 0 ? (
                      <div className="rounded-3xl border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
                        아직 섹션이 없습니다. 「2/4 섹션선택」에서 블록을 추가해 주세요.
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {guidedPhase === 4 ? (
                  pageState.sections.length === 0 ? (
                    <div className="rounded-3xl border border-dashed border-amber-200 bg-amber-50/80 px-4 py-8 text-center text-sm text-amber-950">
                      편집할 섹션이 없습니다. 「2/4 섹션선택」에서 추가한 뒤 다시 와 주세요.
                    </div>
                  ) : (
                    <div className="space-y-4 rounded-3xl border border-slate-200 bg-slate-50/90 p-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600">편집할 섹션</label>
                        <select
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                          value={guidedEditIndex}
                          onChange={(e) => setGuidedEditIndex(Number(e.target.value))}
                        >
                          {pageState.sections.map((section, idx) => {
                            const t = String(section?.content?.title || '').trim()
                            return (
                              <option key={section.id || `${section.type}-${idx}`} value={idx}>
                                {SECTION_TYPE_LABELS[section.type] || section.type}{t ? ` — ${t}` : ''}
                              </option>
                            )
                          })}
                        </select>
                      </div>
                      <div className="border-t border-slate-100 pt-4">
                        {renderSectionEditor(pageState.sections[guidedEditIndex], { variant: 'full' })}
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
                        <button
                          type="button"
                          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                          disabled={guidedEditIndex <= 0}
                          onClick={() => setGuidedEditIndex((n) => Math.max(0, n - 1))}
                        >
                          이전 섹션
                        </button>
                        <span className="text-[11px] text-slate-500">
                          {guidedEditIndex + 1} / {pageState.sections.length}
                        </span>
                        <button
                          type="button"
                          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                          disabled={guidedEditIndex >= pageState.sections.length - 1}
                          onClick={() => setGuidedEditIndex((n) => Math.min(pageState.sections.length - 1, n + 1))}
                        >
                          다음 섹션
                        </button>
                      </div>
                    </div>
                  )
                ) : null}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
